'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Search, Bell, ChevronDown, Menu, X, Building2, LayoutDashboard, MapPin, TrendingUp, BarChart3, GitCompare, BookMarked, FileText, CreditCard, ShieldCheck, Users, LayoutGrid, Layers, ClipboardEdit, Inbox } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export default function Topbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { user, profile, signOut } = useAuth();
  const router = useRouter();

  const handleSignOut = async () => {
    try {
      await signOut();
      router.push('/sign-up-login-screen');
    } catch {
      toast.error('Failed to sign out');
    }
  };

  const handleSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      router.push(`/project-listings-screen?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setMobileOpen(false);
    }
  };

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        const input = document.getElementById('topbar-search') as HTMLInputElement | null;
        input?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const displayName = profile?.full_name || user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'User';
  const initials = displayName.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);
  const plan = profile?.role === 'admin' ? 'Admin' : 'Yearly Plan';
  const isAdmin = profile?.role === 'admin';

  const mobileNavGroups = [
    {
      label: 'Research',
      items: [
        { href: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { href: '/project-listings-screen', label: 'Projects', icon: Building2 },
        { href: '/micro-markets', label: 'Micro Markets', icon: MapPin },
      ],
    },
    {
      label: 'Analytics',
      items: [
        { href: '/price-trends', label: 'Price Trends', icon: TrendingUp },
        { href: '/analytics', label: 'Market Analytics', icon: BarChart3 },
        { href: '/project-comparison', label: 'Compare Projects', icon: GitCompare },
        { href: '/tower-inventory', label: 'Tower Inventory', icon: Layers },
        { href: '/saved', label: 'Saved Reports', icon: BookMarked },
      ],
    },
    {
      label: 'Tools',
      items: [
        { href: '/reports', label: 'PDF Reports', icon: FileText },
        { href: '/alerts', label: 'Alerts', icon: Bell },
        { href: '/subscription', label: 'Subscription', icon: CreditCard },
      ],
    },
    ...(isAdmin
      ? [
          {
            label: 'Admin',
            items: [
              { href: '/admin', label: 'Admin Panel', icon: ShieldCheck },
              { href: '/subscribers', label: 'Subscribers', icon: Users },
              { href: '/admin-dashboard', label: 'Admin Dashboard', icon: LayoutGrid },
              { href: '/admin-inventory', label: 'Inventory Mgmt', icon: ClipboardEdit },
              { href: '/admin-submissions', label: 'Form Submissions', icon: Inbox },
            ],
          },
        ]
      : []),
  ];

  return (
    <>
      <header className="h-14 bg-card border-b border-border flex items-center gap-3 px-4 lg:px-6 shrink-0 z-20">
        {/* Mobile logo + hamburger */}
        <div className="flex items-center gap-2 lg:hidden">
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-all"
            aria-label="Toggle navigation"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
          <AppLogo size={28} />
          <span className="font-semibold text-sm text-foreground">PropdataX</span>
        </div>

        {/* Search — hidden on mobile, visible md+ */}
        <div className="hidden md:flex flex-1 max-w-md">
          <div className="relative w-full">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              id="topbar-search"
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleSearch}
              placeholder="Search projects, developers, locations… ⌘K"
              className="input-base pl-9 py-2 text-sm h-9"
            />
          </div>
        </div>

        <div className="flex-1 hidden md:block" />

        {/* Market indicator */}
        <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
          <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
          <span className="text-xs font-medium text-emerald-400">Pune Market Live</span>
        </div>

        {/* Notifications */}
        <Link href="/alerts" className="relative p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-all">
          <Bell size={18} />
          <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-amber-500 rounded-full" />
        </Link>

        {/* User */}
        <button onClick={handleSignOut} className="flex items-center gap-2 px-2 sm:px-3 py-1.5 rounded-lg hover:bg-muted/60 transition-all" title="Sign out">
          <div className="w-7 h-7 rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center shrink-0">
            <span className="text-xs font-semibold text-amber-400">{initials}</span>
          </div>
          <div className="hidden md:block text-left">
            <p className="text-xs font-medium text-foreground leading-tight">{displayName}</p>
            <p className="text-[10px] text-muted-foreground leading-tight">{plan}</p>
          </div>
          <ChevronDown size={14} className="text-muted-foreground hidden md:block" />
        </button>
      </header>

      {/* Mobile nav drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-background/90 backdrop-blur-sm" onClick={() => setMobileOpen(false)}>
          <div className="w-72 h-full bg-card border-r border-border flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-4 border-b border-border">
              <div className="flex items-center gap-2">
                <AppLogo size={28} />
                <span className="font-semibold text-foreground">PropdataX</span>
              </div>
              <button onClick={() => setMobileOpen(false)} className="p-1.5 rounded-lg hover:bg-muted/60 text-muted-foreground">
                <X size={18} />
              </button>
            </div>

            {/* Mobile search */}
            <div className="px-4 py-3 border-b border-border">
              <div className="relative">
                <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleSearch}
                  placeholder="Search projects…"
                  className="input-base pl-9 py-2 text-sm h-9"
                />
              </div>
            </div>

            <nav className="flex-1 overflow-y-auto py-3 space-y-4 scrollbar-thin">
              {mobileNavGroups.map((group) => (
                <div key={group.label} className="px-2">
                  <p className="px-3 mb-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">
                    {group.label}
                  </p>
                  <div className="space-y-0.5">
                    {group.items.map((item) => (
                      <Link
                        key={`mobile-${item.href}`}
                        href={item.href}
                        onClick={() => setMobileOpen(false)}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-all"
                      >
                        <item.icon size={17} className="shrink-0" />
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </nav>

            {/* User info at bottom */}
            <div className="border-t border-border p-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center shrink-0">
                  <span className="text-xs font-semibold text-amber-400">{initials}</span>
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-foreground truncate">{displayName}</p>
                  <p className="text-[10px] text-muted-foreground">{plan}</p>
                </div>
              </div>
              <button
                onClick={handleSignOut}
                className="mt-3 w-full text-xs text-muted-foreground hover:text-rose-400 hover:bg-rose-500/10 py-2 rounded-lg transition-all text-left px-3"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}